/**
 * [UI] Maps a SOF ReceivingStatus value to the queue/detail status chip.
 * Colors are Ionic theme color names.
 */
export interface StatusChip {
  label: string;
  color: 'warning' | 'info' | 'success' | 'medium';
}

/**
 * Case-insensitive ReceivingStatus → chip mapping:
 * Pending → "To pick" (warning), In Progress → "In progress" (info blue),
 * Complete → "Staged/Complete" (success). Anything else renders as-is in a
 * neutral chip.
 *
 * A BLANK status maps to "Not started" rather than an empty label.
 * `SOF.Orders.ReceivingStatus` is `''` on a SOF nobody has touched, and the
 * view passes that through, so the default branch used to render a chip
 * containing only its coloured dot — unreadable, and indistinguishable from
 * any other unmapped value.
 *
 * @param receivingStatus - The raw ReceivingStatus value from the API.
 * @returns The chip label and Ionic color to render.
 */
export function statusChip(
  receivingStatus: string | null | undefined
): StatusChip {
  const status = (receivingStatus ?? '').trim().toLowerCase();
  switch (status) {
    case 'pending':
      return { label: 'To pick', color: 'warning' };
    case 'in progress':
      // Info blue, not 'secondary'. --ion-color-secondary is #4a5a6a, a slate
      // that at a 12% wash is indistinguishable from the neutral 'medium'
      // chip — so a started SOF looked identical to an untouched one.
      return { label: 'In progress', color: 'info' };
    case 'complete':
      return { label: 'Staged/Complete', color: 'success' };
    case '':
      return { label: 'Not started', color: 'medium' };
    default:
      return { label: receivingStatus ?? '', color: 'medium' };
  }
}
